/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { selectAllResourceChanges } from "../slices/resourceChangeReport/resourceChangeReportSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import { changeTabMenu } from "../slices/tabPane/tabPaneSlice";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import ReferenceEntry, { DisplayFromFHIRReference, displayStringFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import updateChangeReport from "../slices/resourceChangeReport/updateChangeReport";
import Spinner from "../components/Spinner";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { DisplayFromFHIRUri } from "../datatypeComponents/UriEntry";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import DatatypeSelector from "../datatypeComponents/DatatypeSelector";
import CodeEntry from "../datatypeComponents/CodeEntry";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";

const measureDotBasisValues = ['Observation', 'Condition', 'Medication'];
const measureDotScoringSystemChoices = [{ 'uri': 'http://terminology.hl7.org/CodeSystem/measure-scoring', 'display': 'HL7 Measure Scoring CodeSystem' }];
const measureDotScoringUnitSystemChoices = [{ 'uri': 'http://unitsofmeasure.org	', 'display': 'UCUM' }];
const measureDotCompositeScoringSystemChoices = [{ 'uri': 'http://terminology.hl7.org/CodeSystem/composite-measure-scoring', 'display': 'HL7 Composite Measure Scoring CodeSystem' }];
const measureDotTypeSystemChoices = [{ 'uri': 'http://terminology.hl7.org/CodeSystem/measure-type', 'display': 'HL7 Measure Type CodeSystem' }];
const measureDotSubjectAllowedDatatypes = ['CodeableConcept', 'Reference'];
const measureDotSubjectReferenceResourceTypes = ['Group'];
const measureDotSubjectCodeableConceptValueSet = [
    { system: "http://hl7.org/fhir/fhir-types", code: "CareTeam", display: "CareTeam" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Device", display: "Device" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Group", display: "Group" },
    { system: "http://hl7.org/fhir/fhir-types", code: "HealthcareService", display: "HealthcareService" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Location", display: "Location" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Organization", display: "Organization" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Patient", display: "Patient" },
    { system: "http://hl7.org/fhir/fhir-types", code: "Practitioner", display: "Practitioner" },
    { system: "http://hl7.org/fhir/fhir-types", code: "PractitionerRole", display: "PractitionerRole" },
    { system: "http://hl7.org/fhir/fhir-types", code: "RelatedPerson", display: "RelatedPerson" }
];

const generateNarrativeForMeasureResource = (resource) => {
    let status = "generated";
    let innerDiv = "";
    try {
        if (resource.title) {
            innerDiv += "<p><b>Title: </b>" + resource.title + "</p>";
        }
        if (resource.subtitle && typeof resource.type === "string") {
            innerDiv += "<p><b>Subtitle: </b>" + resource.subtitle + "</p>";
        }
        if (resource.name) {
            innerDiv += "<p><b>Name: </b>" + resource.name + "</p>";
        }
        if (resource.description) {
            innerDiv += "<p><b>Description: </b>" + resource.description + "</p>";
        }
        if (resource.subjectCodeableConcept) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRCodeableConcept(resource.subjectCodeableConcept) + "</p>";
        }
        if (resource.subjectReference) {
            innerDiv += "<p><b>Subject: </b>" + displayStringFromFHIRReference(resource.subjectReference) + "</p>";
        }
        if (resource.basis) {
            innerDiv += "<p><b>Basis: </b>" + resource.basis + "</p>";
        }
        if (resource.library?.length > 0) {
            innerDiv += resource.library.map((item) => {
                return "<p><b>Library: </b>" + item + "</p>"
            }).join("");
        }
        if (resource.disclaimer) {
            innerDiv += "<p><b>Disclaimer: </b>" + resource.disclaimer + "</p>";
        }
        if (resource.scoring) {
            innerDiv += "<p><b>Scoring: </b>" + displayStringFromFHIRCodeableConcept(resource.scoring) + "</p>";
        }
        if (resource.scoringUnit) {
            innerDiv += "<p><b>Scoring Unit: </b>" + displayStringFromFHIRCodeableConcept(resource.scoringUnit) + "</p>";
        }
        if (resource.compositeScoring) {
            innerDiv += "<p><b>Composite Scoring: </b>" + displayStringFromFHIRCodeableConcept(resource.compositeScoring) + "</p>";
        }
        if (resource.type?.length > 0) {
            innerDiv += resource.type.map((item) => {
                return "<p><b>Measure Type: </b>" + displayStringFromFHIRCodeableConcept(item) + "</p>"
            }).join("");
        }
        if (resource.riskAdjustment) {
            innerDiv += "<p><b>Risk Adjustment: </b>" + resource.riskAdjustment + "</p>";
        }
        if (resource.rateAggregation) {
            innerDiv += "<p><b>Rate Aggregation: </b>" + resource.rateAggregation + "</p>";
        }
        if (resource.rationale) {
            innerDiv += "<p><b>Rationale: </b>" + resource.rationale + "</p>";
        }
        if (resource.clinicalRecommendationStatement) {
            innerDiv += "<p><b>Clinical Recommendation Statement: </b>" + resource.clinicalRecommendationStatement + "</p>";
        }
        if (resource.improvementNotation) {
            innerDiv += "<p><b>Improvement Notation: </b>" + displayStringFromFHIRCodeableConcept(resource.improvementNotation) + "</p>";
        }
        if (resource.guidance) {
            innerDiv += "<p><b>Guidance: </b>" + resource.guidance + "</p>";
        }
    } catch {
        innerDiv = "[Unable to generate Narrative Summary.]"
    }
    return { "status": status, "div": "<div xmlns=\"http://www.w3.org/1999/xhtml\">" + innerDiv + "</div>" };
}

const MeasureTermEntry = ({ elementName, fieldLabel, startingValue,
    setResourceState, startCollapsed }) => {

    const [measureTermState, setMeasureTermState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingTerm = { code: {}, definition: "" };
        if (!startingValue) {
            startingTerm = {};
        } else {
            if (startingValue.extension) { startingTerm.extension = startingValue.extension; }
            if (startingValue.code) { startingTerm.code = startingValue.code; }
            if (startingValue.definition) { startingTerm.definition = startingValue.definition; }
        }
        setMeasureTermState(JSON.parse(JSON.stringify(startingTerm)));
    }, []);

    useEffect((() => {
        if (Object.keys(measureTermState).length) {
            let newMeasureTerm = {};
            if (measureTermState.extension) { newMeasureTerm.extension = measureTermState.extension; }
            if (measureTermState.code && Object.keys(measureTermState.code).length) {
                newMeasureTerm.code = measureTermState.code;
            }
            if (measureTermState.definition) { newMeasureTerm.definition = measureTermState.definition; }
            if (Object.keys(newMeasureTerm).length === 0) {
                newMeasureTerm = null;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newMeasureTerm }; });
        }
    }), [measureTermState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.code && <span><b>Term code: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.code} /></span>}
                {startingValue.definition && <span><b>Term definition: </b><DisplayFromFHIRMarkdown markdown={startingValue.definition} /></span>}
            </div>
            }
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(measureTermState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='code' fieldLabel='Term code'
                    startingValue={measureTermState.code} setResourceState={setMeasureTermState} />
                <MarkdownEntry elementName='definition' fieldLabel='Term definition'
                    startingValue={measureTermState.definition} setResourceState={setMeasureTermState} />
            </div>
        </>
    }
};

const MeasureDisplay = ({ foi }) => {

    const dispatch = useDispatch();
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceChanges = useSelector(state => selectAllResourceChanges(state));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));

    const [measureResourceState, setMeasureResourceState] = useState({});
    const [subjectDatatypeState, setSubjectDatatypeState] = useState("none");

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Summary", "Title", "Measure Definition"]));
        dispatch(changeBrowserTabTitle("FEvIR Measure - " + displayTitle));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setMeasureResourceState(olderVersionJson);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            setMeasureResourceState(resourceJson);
            if (resourceJson.subjectReference) { setSubjectDatatypeState("Reference"); }
            else if (resourceJson.subjectCodeableConcept) { setSubjectDatatypeState("CodeableConcept"); }
        }
    }, [resourceJson]);

    useEffect(() => {
        dispatch(changeTool("FEvIR®: Measure " + (editMode ? "Builder" : "Viewer")));
    }, [editMode]);

    useEffect((() => {
        if (subjectDatatypeState === 'CodeableConcept') {
            setMeasureResourceState(prevState => { return { ...prevState, 'subjectReference': null } });
        }
        if (subjectDatatypeState === 'Reference') {
            setMeasureResourceState(prevState => { return { ...prevState, 'subjectCodeableConcept': null } });
        }
    }), [subjectDatatypeState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(measureResourceState, "text", "Narrative", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.title;
            previousFinalValue = newResourceDetail.revisedResourceJson.title;
            newResourceDetail.revisedResourceJson.title = measureResourceState.title;
            newResourceDetail.displayTitle = measureResourceState.title ||
                measureResourceState.name || newResourceDetail.displayTitle;
            if (measureResourceState.title !== undefined &&
                previousFinalValue !== measureResourceState.title) {
                console.log("resourceUpdate for Measure.title");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "title",
                    previousInitialValue,
                    measureResourceState.title);
            }
        }
    }, [measureResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousInitialValue = null;
            let previousFinalValue = null;
            previousInitialValue = newResourceDetail.resourceJson.name;
            previousFinalValue = newResourceDetail.revisedResourceJson.name;
            newResourceDetail.revisedResourceJson.name = measureResourceState.name;
            newResourceDetail.displayTitle = measureResourceState.title ||
                measureResourceState.name || newResourceDetail.displayTitle;
            if (measureResourceState.name !== undefined &&
                previousFinalValue !== measureResourceState.name) {
                console.log("resourceUpdate for Measure.name");
                dispatch(resourceUpdated(newResourceDetail));
                updateChangeReport(dispatch, resourceChanges, newResourceDetail.id,
                    "string",
                    "name",
                    previousInitialValue,
                    measureResourceState.name);
            }
        }
    }, [measureResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "subtitle", "string", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.subtitle]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "subjectCodeableConcept", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.subjectCodeableConcept]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "subjectReference", "Reference", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.subjectReference]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "description", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "library", "ArrayOfUri", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.library]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "basis", "code", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.basis]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "disclaimer", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.disclaimer]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "scoring", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.scoring]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "scoringUnit", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.scoringUnit]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "compositeScoring", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.compositeScoring]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "type", "ArrayOfCodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.type]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "riskAdjustment", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.riskAdjustment]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "rateAggregation", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.rateAggregation]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "rationale", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.rationale]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "clinicalRecommendationStatement", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.clinicalRecommendationStatement]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "improvementNotation", "CodeableConcept", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.improvementNotation]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "term", "ArrayOfMeasureTerm", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.term]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(measureResourceState, "guidance", "markdown", parentResourceDetail,
                resourceChanges, dispatch);
        }
    }, [measureResourceState.guidance]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {measureResourceState.id && <>
            <div style={{ marginTop: "12px" }}>
                <h3 id="summary">Summary</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                generatedNarrative={generateNarrativeForMeasureResource(measureResourceState)} startCollapsed
                                startingValue={measureResourceState.text} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {(measureResourceState.text?.div && measureResourceState.text.status && measureResourceState.text.status !== "empty") ?
                                <DisplayFromFHIRXhtml xhtml={measureResourceState.text.div} />
                                : <p>[Narrative summary not generated.]</p>}
                        </>}
                </div>
                <h3 id="title">Title</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <StringEntry elementName='title' fieldLabel='Title'
                                startingValue={measureResourceState.title} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.title && <p><b>Title: </b>{measureResourceState.title}</p>}
                        </>}
                    {editMode ?
                        <>
                            <StringEntry elementName='subtitle' fieldLabel='Subtitle'
                                startingValue={measureResourceState.subtitle} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.subtitle && <p><b>Subtitle: </b>{measureResourceState.subtitle}</p>}
                        </>}
                    {editMode ?
                        <>
                            <StringEntry elementName='name' fieldLabel='Name'
                                startingValue={measureResourceState.name} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.name && <p><b>Name: </b>{measureResourceState.name}</p>}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='description' fieldLabel='Description'
                                startCollapsed
                                startingValue={measureResourceState.description} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.description} /><br /><br /></div>}
                        </>}
                </div>
                <h3 id="measure-definition">Measure Definition</h3>
                <div style={{ marginLeft: "24px" }}>
                    {editMode ?
                        <>
                            <p><b>Subject:</b></p>
                            <div style={{ marginLeft: "24px" }}>
                                <DatatypeSelector elementXName='subject[x]' allowedDatatypes={measureDotSubjectAllowedDatatypes}
                                    datatypeState={subjectDatatypeState} setDatatypeState={setSubjectDatatypeState} />
                                {(subjectDatatypeState === 'CodeableConcept') &&
                                    <CodeableConceptEntry elementName='subjectCodeableConcept'
                                        fieldLabel='Subject (as CodeableConcept)' startCollapsed
                                        startingValue={measureResourceState.subjectCodeableConcept || null}
                                        valueSet={measureDotSubjectCodeableConceptValueSet} setResourceState={setMeasureResourceState} />}
                                {(subjectDatatypeState === 'Reference') &&
                                    <ReferenceEntry elementName='subjectReference' fieldLabel='Subject (as Reference)'
                                        referencedResourceTypes={measureDotSubjectReferenceResourceTypes} startCollapsed
                                        startingValue={measureResourceState.subjectReference || null} setResourceState={setMeasureResourceState} />}
                            </div>
                        </>
                        :
                        <>
                            {measureResourceState.subjectCodeableConcept && <span><b>Subject: </b><DisplayFromFHIRCodeableConcept codeableConcept={measureResourceState.subjectCodeableConcept} /></span>}
                            {measureResourceState.subjectReference && <p><b>Subject: </b><DisplayFromFHIRReference reference={measureResourceState.subjectReference} /></p>}
                        </>}
                    {editMode ?
                        <>
                            <CodeEntry elementName='basis' fieldLabel='Basis'
                                startingValue={measureResourceState.basis} setResourceState={setMeasureResourceState}
                                allowedValues={measureDotBasisValues}
                                dataEntryStyle='dropdown' />
                        </>
                        :
                        <>
                            {measureResourceState.basis && <p><b>Basis: </b>{measureResourceState.basis}</p>}
                        </>}
                    {editMode ?
                        <>
                            <ArrayEntry datatype='uri' elementName='library'
                                fieldLabel='Library (canonical URL)'
                                startingValue={measureResourceState.library} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(measureResourceState.library) && measureResourceState.library.map((library, libraryIndex) => {
                                return <div key={libraryIndex}><b>Library: </b><DisplayFromFHIRUri uri={library} /><br /></div>
                            })}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='disclaimer' fieldLabel='Disclaimer'
                                startingValue={measureResourceState.disclaimer} startCollapsed
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.disclaimer && <div><b>Disclaimer: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.disclaimer} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <>
                            <CodeableConceptEntry elementName='scoring' fieldLabel='Scoring'
                                startingValue={measureResourceState.scoring} startCollapsed
                                setResourceState={setMeasureResourceState}
                                systemChoices={measureDotScoringSystemChoices} systemChoicesOpen={true} />
                        </>
                        :
                        <>
                            {measureResourceState.scoring && <span><b>Scoring: </b><DisplayFromFHIRCodeableConcept codeableConcept={measureResourceState.scoring} /><br /><br /></span>}
                        </>}
                    {editMode ?
                        <>     <CodeableConceptEntry elementName='scoringUnit' fieldLabel='Scoring Unit'
                            startingValue={measureResourceState.scoringUnit} startCollapsed
                            setResourceState={setMeasureResourceState}
                            systemChoices={measureDotScoringUnitSystemChoices} systemChoicesOpen={true} />

                        </>
                        :
                        <>
                            {measureResourceState.scoringUnit && <span><b>Scoring Unit: </b><DisplayFromFHIRCodeableConcept codeableConcept={measureResourceState.scoringUnit} /><br /><br /></span>}
                        </>}
                    {editMode ?
                        <>   <CodeableConceptEntry elementName='compositeScoring' fieldLabel='Composite Scoring'
                            startingValue={measureResourceState.compositeScoring} startCollapsed
                            setResourceState={setMeasureResourceState}
                            systemChoices={measureDotCompositeScoringSystemChoices} systemChoicesOpen={true} />
                        </>
                        :
                        <>
                            {measureResourceState.compositeScoring && <span><b>Composite Scoring: </b><DisplayFromFHIRCodeableConcept codeableConcept={measureResourceState.compositeScoring} /><br /><br /></span>}
                        </>}
                    {editMode ?
                        <>
                            <ArrayEntry datatype='CodeableConcept' elementName='type' fieldLabel='Measure Type'
                                startingValue={measureResourceState.type} systemChoices={measureDotTypeSystemChoices}
                                systemChoicesOpen={true} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {Array.isArray(measureResourceState.type) &&
                                measureResourceState.type.map((type, typeIndex) => {
                                    return <div key={typeIndex}><div><b>Measure Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /></div></div>
                                })}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='riskAdjustment' fieldLabel='Risk Adjustment'
                                startingValue={measureResourceState.riskAdjustment} startCollapsed
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.riskAdjustment && <div><b>Risk Adjustment: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.riskAdjustment} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='rateAggregation' fieldLabel='Rate Aggregation'
                                startingValue={measureResourceState.rateAggregation} startCollapsed
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.rateAggregation && <div><b>Rate Aggregation: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.rateAggregation} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='rationale' fieldLabel='Rationale'
                                startingValue={measureResourceState.rationale} startCollapsed
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.rationale && <div><b>Rationale: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.rationale} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='clinicalRecommendationStatement'
                                fieldLabel='Clinical Recommendation Statement' startCollapsed
                                startingValue={measureResourceState.clinicalRecommendationStatement}
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.clinicalRecommendationStatement && <div><b>Clinical Recommendation Statement: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.clinicalRecommendationStatement} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <>
                            <CodeableConceptEntry elementName='improvementNotation'
                                fieldLabel='Improvement Notation' startCollapsed
                                startingValue={measureResourceState.improvementNotation}
                                setResourceState={setMeasureResourceState} systemChoicesOpen={true} />
                        </>
                        :
                        <>
                            {measureResourceState.improvementNotation && <span><b>Improvement Notation: </b><DisplayFromFHIRCodeableConcept codeableConcept={measureResourceState.improvementNotation} /><br /><br /></span>}
                        </>}
                    {editMode ?
                        <>
                            <ArrayEntry datatype='MeasureTerm' elementName='term'
                                fieldLabel='Term'
                                startingValue={measureResourceState.term} setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.term?.length > 0 && <div>
                                <p>{measureResourceState.term.length === 1 ? <b>Term: </b> : <b>Terms: </b>}</p>
                                <div style={{ marginLeft: "24px" }}>
                                    {measureResourceState.term.map((term, termIndex) => {
                                        return <div key={termIndex}>
                                            <p><b>Term entry {termIndex + 1}</b></p>
                                            <div style={{ marginLeft: "24px" }}>
                                                {term.code && <span><b>Term code: </b><DisplayFromFHIRCodeableConcept codeableConcept={term.code} /></span>}
                                                {term.definition && <span><b>Term definition: </b><DisplayFromFHIRMarkdown markdown={term.definition} /></span>}
                                            </div>
                                        </div>
                                    })}
                                </div>
                            </div>
                            }
                        </>}
                    {editMode ?
                        <>
                            <MarkdownEntry elementName='guidance' fieldLabel='Guidance'
                                startingValue={measureResourceState.guidance} startCollapsed
                                setResourceState={setMeasureResourceState} />
                        </>
                        :
                        <>
                            {measureResourceState.guidance && <div><b>Guidance: </b><DisplayFromFHIRMarkdown markdown={measureResourceState.guidance} /><br /><br /></div>}
                        </>}
                    {editMode ?
                        <> <p>Placeholder - data entry for group BackboneElement</p></>
                        :
                        <>
                            {(Array.isArray(measureResourceState.group) && measureResourceState.group.length > 0) && <p>
                                Placeholder -- Measure.group data will be displayed here
                            </p>}
                        </>}
                    {editMode ?
                        <>     <p>Placeholder - data entry for supplementalData BackboneElement</p></>
                        :
                        <>
                            {(Array.isArray(measureResourceState.supplementalData) && measureResourceState.supplementalData.length > 0) && <p>
                                Placeholder -- Measure.supplementalData data will be displayed here
                            </p>}
                        </>}
                </div>
            </div>
        </>}
    </div>
}

export default MeasureDisplay;
export { MeasureTermEntry };